import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Alert, AlertTitle, Chip, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { get } from "../utils/networkUtils";
import { setAlerts, setLoading, setError } from "../store/slices/alertsSlice";

const AlertsContainer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { alerts } = useSelector((state) => state.alerts);
    const [dismissedAlerts, setDismissedAlerts] = React.useState(new Set());

    useEffect(() => {
        const fetchAlerts = async () => {
            dispatch(setLoading(true));
            try {
                const response = await get("admin/alerts");
                dispatch(setAlerts(response.alerts || []));
            } catch (err) {
                console.error("Error fetching alerts:", err);
                dispatch(setError(err.message || "Failed to fetch alerts"));
            }
        };

        fetchAlerts();

        // Poll for new alerts every 60 seconds
        const interval = setInterval(fetchAlerts, 60000);

        return () => clearInterval(interval);
    }, [dispatch]);

    const getSeverityColor = (severity) => {
        switch (severity) {
            case "critical":
            case "high":
                return "error";
            case "medium":
                return "warning";
            case "low":
            case "info":
                return "info";
            default:
                return "info";
        }
    };

    const getCategoryColor = (category) => {
        switch (category) {
            case "system":
                return "primary";
            case "user":
                return "success";
            case "security":
                return "error";
            case "feed":
                return "warning";
            case "database":
                return "info";
            default:
                return "default";
        }
    };

    const handleDismiss = (alertId) => {
        setDismissedAlerts((prev) => new Set([...prev, alertId]));
    };

    const handleNavigate = (alert, e) => {
        if (e) {
            e.stopPropagation();
        }
        if (alert.route) {
            navigate(alert.route);
            handleDismiss(alert.id);
        }
    };

    // Filter alerts by severity and exclude dismissed ones
    const sortedAlerts = alerts
        .filter((alert) => !dismissedAlerts.has(alert.id))
        .sort((a, b) => {
            const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
            return severityOrder[a.severity] - severityOrder[b.severity];
        });

    if (sortedAlerts.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                position: "fixed",
                top: 16,
                right: 16,
                zIndex: 9999,
                maxWidth: 400,
                display: "flex",
                flexDirection: "column",
                gap: 1,
            }}
        >
            {sortedAlerts.map((alert) => (
                <Alert
                    key={alert.id}
                    severity={getSeverityColor(alert.severity)}
                    sx={{
                        boxShadow: 3,
                    }}
                    action={
                        <IconButton
                            size="small"
                            aria-label="close"
                            color="inherit"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDismiss(alert.id);
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    }
                >
                    <AlertTitle>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <span>{alert.message}</span>
                            <Chip
                                label={alert.category}
                                size="small"
                                color={getCategoryColor(alert.category)}
                                sx={{ textTransform: "capitalize", fontSize: "0.7rem" }}
                            />
                        </Box>
                    </AlertTitle>
                    {alert.metadata && Object.keys(alert.metadata).length > 0 && (
                        <Box sx={{ fontSize: "0.875rem", opacity: 0.9, mt: 0.5 }}>
                            {Object.entries(alert.metadata).map(([key, value]) => (
                                <Box key={key} component="span" sx={{ display: "block" }}>
                                    {key}: <strong>{typeof value === 'object' ? JSON.stringify(value) : value}</strong>
                                </Box>
                            ))}
                        </Box>
                    )}
                    {alert.route && (
                        <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                            <Chip
                                label="View Details"
                                size="small"
                                icon={<ArrowForwardIcon sx={{ fontSize: "0.875rem" }} />}
                                onClick={(e) => handleNavigate(alert, e)}
                                sx={{
                                    fontSize: "0.75rem",
                                    height: "24px",
                                    cursor: "pointer",
                                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                                    color: "inherit",
                                    "& .MuiChip-icon": {
                                        color: "inherit",
                                        marginLeft: "8px",
                                        marginRight: "-4px",
                                    },
                                    "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.35)",
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Alert>
            ))}
        </Box>
    );
};

export default AlertsContainer;
